import React, { useState } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { useAuth } from '../context/AuthContext';

const { FiLock, FiMail, FiEye, FiEyeOff, FiLoader, FiCpu, FiAlertTriangle } = FiIcons;

export default function Login() {
  const { signIn } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim() || !password) return;
    setError('');
    setLoading(true);
    try {
      await signIn(email.trim(), password);
      // On success the AuthProvider's onAuthStateChange swaps in the app.
    } catch (err) {
      // Generic message — don't reveal whether the email exists.
      setError(err?.message || 'Invalid login credentials');
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#000000] lg:bg-[#0a0a0b] flex items-center justify-center p-4 font-sans">
      <motion.div
        initial={{ opacity: 0, y: 20, scale: 0.97 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        className="w-full max-w-sm bg-[#1c1c1d] border border-[#2c2c2e] rounded-2xl p-6 shadow-2xl shadow-black/50 relative overflow-hidden"
      >
        <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-blue-500 to-purple-500" />

        <div className="flex flex-col items-center text-center mb-6 mt-1">
          <div className="w-14 h-14 rounded-2xl bg-blue-500/20 border border-blue-500/30 flex items-center justify-center text-blue-400 mb-3">
            <SafeIcon icon={FiCpu} className="text-2xl" />
          </div>
          <h1 className="text-white font-bold text-xl">Solana Bot</h1>
          <p className="text-gray-500 text-xs mt-1">Sign in to access your trading dashboard</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-3">
          <div>
            <label className="block text-[10px] font-semibold text-gray-500 uppercase tracking-wider mb-1.5">Email</label>
            <div className="relative">
              <SafeIcon icon={FiMail} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 text-sm" />
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="username"
                placeholder="you@example.com"
                className="w-full bg-[#0a0a0b] border border-[#3c3c3e] rounded-xl py-2.5 pl-9 pr-3 text-white text-sm focus:outline-none focus:border-blue-500 transition-colors"
              />
            </div>
          </div>

          <div>
            <label className="block text-[10px] font-semibold text-gray-500 uppercase tracking-wider mb-1.5">Password</label>
            <div className="relative">
              <SafeIcon icon={FiLock} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 text-sm" />
              <input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="current-password"
                placeholder="••••••••"
                className="w-full bg-[#0a0a0b] border border-[#3c3c3e] rounded-xl py-2.5 pl-9 pr-10 text-white text-sm focus:outline-none focus:border-blue-500 transition-colors"
              />
              <button
                type="button"
                onClick={() => setShowPassword(v => !v)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-300 transition-colors"
                tabIndex={-1}
              >
                <SafeIcon icon={showPassword ? FiEyeOff : FiEye} className="text-sm" />
              </button>
            </div>
          </div>

          {error && (
            <div className="flex items-start gap-2 bg-red-500/10 border border-red-500/30 rounded-lg px-3 py-2 text-red-400 text-xs">
              <SafeIcon icon={FiAlertTriangle} className="mt-0.5 flex-shrink-0" />
              <span>{error}</span>
            </div>
          )}

          <button
            type="submit"
            disabled={loading || !email.trim() || !password}
            className="w-full font-bold py-3 rounded-xl bg-blue-600 hover:bg-blue-500 disabled:bg-[#2c2c2e] disabled:text-gray-500 text-white transition-colors flex items-center justify-center gap-2 shadow-lg shadow-blue-500/20"
          >
            {loading ? (
              <><SafeIcon icon={FiLoader} className="animate-spin" /> Signing in...</>
            ) : (
              <><SafeIcon icon={FiLock} /> Sign In</>
            )}
          </button>
        </form>

        <p className="text-center text-[10px] text-gray-600 mt-5">
          Private dashboard · Access restricted
        </p>
      </motion.div>
    </div>
  );
}
